import React, { useState } from 'react';
import { Car, BarChart3, Users, Calendar, Database, TrendingUp, CheckCircle, ArrowRight, Menu, X, Clock, DollarSign, Gauge, Lock, Cloud, ChevronDown, Bell, Shield, Zap } from 'lucide-react';
import 'flag-icons/css/flag-icons.min.css';

type Language = 'en' | 'fr' | 'ar';

const translations = {
  en: {
    nav: { features: 'Features', benefits: 'Benefits', pricing: 'Pricing', login: 'Log in', getStarted: 'Get Started' },
    hero: {
      tagline: 'Complete ERP Solution for Car Rental Agencies',
      title1: 'Manage your', title2: 'rental business', title3: 'effortlessly',
      description: 'From bookings and fleet tracking to lead management and employee oversight—karia.tn delivers everything you need with elegant data visualization.',
      cta: 'Try Live Demo', ctaSubtext: 'No signup required • Instant access', watchDemo: 'Schedule a Demo', trusted: 'Trusted by rental agencies worldwide',
      quickSetup: 'Quick Setup', secure: 'Secure & Reliable', support: '24/7 Support',
    },
    demoForm: {
      title: 'Schedule a Demo',
      subtitle: 'See how karia.tn can transform your rental business',
      agencyName: 'Agency Name',
      yourName: 'Your Name',
      yourRole: 'Your Role',
      fleetSize: 'Fleet Size',
      phone: 'Phone Number',
      email: 'Email',
      preferredTime: 'Preferred Callback Time',
      submit: 'Request Demo',
      submitting: 'Submitting...',
      success: 'Thank you! We\'ll contact you soon.',
      error: 'Something went wrong. Please try again.',
      roles: { owner: 'Owner', manager: 'Manager', operations: 'Operations', other: 'Other' },
      fleetSizes: { xs: '1-10 cars', sm: '11-25 cars', md: '26-50 cars', lg: '51-100 cars', xl: '100+ cars' },
      selectRole: 'Select role',
      selectFleetSize: 'Select size',
      phonePlaceholder: 'XX XXX XXX',
      selectTime: 'Select time slot',
      timeSlots: { morning: 'Morning (9AM - 12PM)', afternoon: 'Afternoon (12PM - 3PM)', evening: 'Evening (3PM - 6PM)', anytime: 'Anytime' },
    },
    dashboard: { title: 'karia.tn Dashboard', activeRentals: 'Active Rentals', fleetSize: 'Fleet Size', newLeads: 'New Leads', thisMonth: 'This Month', bookingActivity: 'Booking Activity', week: 'Week' },
    features: {
      label: 'Features', title: 'Everything you need', description: 'Comprehensive tools designed specifically for car rental agencies',
      items: [
        { number: '01', title: 'Rental Management', desc: 'Create and track bookings from start to finish. Automated reminders, flexible scheduling, and complete rental lifecycle management in one place.' },
        { number: '02', title: 'Fleet Tracking', desc: 'Monitor your entire vehicle inventory in real-time. Track availability, maintenance schedules, and vehicle status. Import pre-ready car databases instantly.' },
        { number: '03', title: 'Lead & Client Management', desc: 'Collect leads efficiently and store detailed client information. Build comprehensive customer profiles and maintain relationships that drive repeat business.' },
        { number: '04', title: 'Employee Management', desc: 'Organize your team effectively with role-based access, schedule tracking, and performance monitoring. Keep everyone aligned and productive.' },
        { number: '05', title: 'Data Visualization', desc: 'Transform complex data into beautiful, actionable insights. Clean dashboards that make understanding your business metrics effortless.' },
        { number: '06', title: 'Cloud-Based Access', desc: 'Access your entire operation from anywhere. Secure cloud infrastructure ensures your data is always available and protected.' },
      ],
    },
    benefits: {
      label: 'Why Choose karia.tn', title: 'Built for rental agencies',
      items: [
        { title: 'Save Time', desc: 'Automate repetitive tasks and focus on growing your business' },
        { title: 'Increase Revenue', desc: 'Better tracking and insights lead to optimized pricing and utilization' },
        { title: 'Improve Efficiency', desc: 'Streamline operations across booking, fleet, and team management' },
        { title: 'Enterprise Security', desc: 'Bank-level encryption keeps your business data safe and secure' },
      ],
      documentation: 'Complete Documentation', documentationDesc: 'Track every detail from booking to return with comprehensive digital records.',
      analytics: 'Real-Time Analytics', analyticsDesc: 'Make data-driven decisions with live insights into your operations.',
    },
    pricing: {
      label: 'Pricing', title: 'Simple, transparent pricing', description: 'Start free, upgrade as you grow',
      plans: [
        { name: 'Starter', price: 'Free', desc: 'Perfect for getting started', features: ['Up to 20 vehicles', 'Basic booking management', 'Client database', 'Email support'] },
        { name: 'Professional', currency: 'د.ت', price: '69', period: '/month', desc: 'For growing businesses', features: ['Unlimited vehicles', 'Advanced analytics', 'Employee management', 'Priority support', 'Custom integrations'], featured: true },
        { name: 'Enterprise', price: 'Custom', desc: 'For large operations', features: ['Everything in Professional', 'Dedicated account manager', 'Custom development', 'SLA guarantee'] },
      ],
    },
    testimonials: {
      label: 'Testimonials', title: 'Loved by rental agencies',
      items: [
        { quote: 'karia.tn transformed how we manage our fleet. The interface is clean and our team adapted immediately.', author: 'Sarah Chen', role: 'Operations Manager' },
        { quote: "Finally, an ERP that doesn't overcomplicate things. Everything we need, nothing we don't.", author: 'Michael Roberts', role: 'Agency Owner' },
        { quote: 'The data visualizations alone are worth it. We can see our entire business at a glance.', author: 'Emma Wilson', role: 'Business Analyst' },
      ],
    },
    cta: { title: 'Ready to get started?', description: 'Join rental agencies already transforming their operations with karia.tn. See our live demo today—no signup required.', primary: 'See Live Demo', secondary: 'Schedule Demo' },
    footer: { tagline: 'Complete ERP solution for modern car rental agencies.', product: 'Product', company: 'Company', legal: 'Legal', features: 'Features', pricingLink: 'Pricing', updates: 'Updates', about: 'About', blog: 'Blog', contact: 'Contact', privacy: 'Privacy', terms: 'Terms', security: 'Security', rights: '© 2026 karia.tn. All rights reserved.' },
  },
  fr: {
    nav: { features: 'Fonctionnalités', benefits: 'Avantages', pricing: 'Tarifs', login: 'Connexion', getStarted: 'Commencer' },
    hero: {
      tagline: 'Solution ERP Complète pour Agences de Location de Voitures',
      title1: 'Gérez votre', title2: 'entreprise de location', title3: 'sans effort',
      description: 'Des réservations au suivi de flotte, en passant par la gestion des prospects et des employés—karia.tn vous offre tout ce dont vous avez besoin avec une visualisation élégante des données.',
      cta: 'Essayer la Démo', ctaSubtext: 'Sans inscription • Accès instantané', watchDemo: 'Planifier une Démo', trusted: 'Approuvé par les agences de location du monde entier',
      quickSetup: 'Installation Rapide', secure: 'Sécurisé et Fiable', support: 'Support 24/7',
    },
    demoForm: {
      title: 'Planifier une Démo',
      subtitle: 'Découvrez comment karia.tn peut transformer votre activité',
      agencyName: 'Nom de l\'Agence',
      yourName: 'Votre Nom',
      yourRole: 'Votre Fonction',
      fleetSize: 'Taille de Flotte',
      phone: 'Téléphone',
      email: 'Email',
      preferredTime: 'Heure de Rappel',
      submit: 'Demander une Démo',
      submitting: 'Envoi...',
      success: 'Merci ! Nous vous contacterons bientôt.',
      error: 'Erreur. Veuillez réessayer.',
      roles: { owner: 'Propriétaire', manager: 'Gérant', operations: 'Opérations', other: 'Autre' },
      fleetSizes: { xs: '1-10', sm: '11-25', md: '26-50', lg: '51-100', xl: '100+' },
      selectRole: 'Sélectionner',
      selectFleetSize: 'Sélectionner',
      phonePlaceholder: 'XX XXX XXX',
      selectTime: 'Choisir le créneau',
      timeSlots: { morning: 'Matin (9h - 12h)', afternoon: 'Après-midi (12h - 15h)', evening: 'Soir (15h - 18h)', anytime: 'N\'importe quand' },
    },
    dashboard: { title: 'Tableau de Bord karia.tn', activeRentals: 'Locations Actives', fleetSize: 'Taille de la Flotte', newLeads: 'Nouveaux Prospects', thisMonth: 'Ce Mois', bookingActivity: 'Activité des Réservations', week: 'Semaine' },
    features: {
      label: 'Fonctionnalités', title: 'Tout ce dont vous avez besoin', description: 'Outils complets conçus spécifiquement pour les agences de location de voitures',
      items: [
        { number: '01', title: 'Gestion des Locations', desc: 'Créez et suivez les réservations du début à la fin. Rappels automatiques, planification flexible et gestion complète du cycle de location en un seul endroit.' },
        { number: '02', title: 'Suivi de Flotte', desc: 'Surveillez l\'ensemble de votre inventaire de véhicules en temps réel. Suivez la disponibilité, les calendriers de maintenance et l\'état des véhicules.' },
        { number: '03', title: 'Gestion des Prospects et Clients', desc: 'Collectez efficacement les prospects et stockez les informations clients détaillées. Créez des profils clients complets et maintenez des relations durables.' },
        { number: '04', title: 'Gestion des Employés', desc: 'Organisez efficacement votre équipe avec un accès basé sur les rôles, le suivi des horaires et le monitoring des performances.' },
        { number: '05', title: 'Visualisation des Données', desc: 'Transformez des données complexes en insights visuels et actionnables. Des tableaux de bord clairs qui facilitent la compréhension de vos métriques.' },
        { number: '06', title: 'Accès Cloud', desc: 'Accédez à toute votre opération de n\'importe où. Une infrastructure cloud sécurisée garantit que vos données sont toujours disponibles.' },
      ],
    },
    benefits: {
      label: 'Pourquoi Choisir karia.tn', title: 'Conçu pour les agences de location',
      items: [
        { title: 'Gagnez du Temps', desc: 'Automatisez les tâches répétitives et concentrez-vous sur la croissance de votre entreprise' },
        { title: 'Augmentez vos Revenus', desc: 'Un meilleur suivi et des insights mènent à une tarification et une utilisation optimisées' },
        { title: 'Améliorez l\'Efficacité', desc: 'Rationalisez les opérations de réservation, de flotte et de gestion d\'équipe' },
        { title: 'Sécurité Entreprise', desc: 'Un cryptage de niveau bancaire protège vos données commerciales' },
      ],
      documentation: 'Documentation Complète', documentationDesc: 'Suivez chaque détail de la réservation au retour avec des enregistrements numériques complets.',
      analytics: 'Analyses en Temps Réel', analyticsDesc: 'Prenez des décisions basées sur les données avec des insights en direct sur vos opérations.',
    },
    pricing: {
      label: 'Tarifs', title: 'Tarification simple et transparente', description: 'Commencez gratuitement, évoluez selon vos besoins',
      plans: [
        { name: 'Démarrage', price: 'Gratuit', desc: 'Parfait pour commencer', features: ['Jusqu\'à 20 véhicules', 'Gestion de base des réservations', 'Base de données clients', 'Support par email'] },
        { name: 'Professionnel', currency: '€', price: '99', period: '/mois', desc: 'Pour les entreprises en croissance', features: ['Véhicules illimités', 'Analyses avancées', 'Gestion des employés', 'Support prioritaire', 'Intégrations personnalisées'], featured: true },
        { name: 'Entreprise', price: 'Sur Mesure', desc: 'Pour les grandes opérations', features: ['Tout du Professionnel', 'Gestionnaire de compte dédié', 'Développement personnalisé', 'Garantie SLA'] },
      ],
    },
    testimonials: {
      label: 'Témoignages', title: 'Adopté par les agences de location',
      items: [
        { quote: 'karia.tn a transformé notre façon de gérer notre flotte. L\'interface est claire et notre équipe s\'est adaptée immédiatement.', author: 'Sophie Martin', role: 'Directrice des Opérations' },
        { quote: 'Enfin un ERP qui ne complique pas les choses. Tout ce dont nous avons besoin, rien de superflu.', author: 'Jean Dupont', role: 'Propriétaire d\'Agence' },
        { quote: 'Les visualisations de données seules valent le coup. Nous voyons toute notre activité d\'un coup d\'œil.', author: 'Marie Leroy', role: 'Analyste Commercial' },
      ],
    },
    cta: { title: 'Prêt à commencer?', description: 'Rejoignez les agences de location qui transforment déjà leurs opérations avec karia.tn. Découvrez notre démo en direct aujourd\'hui—sans inscription requise.', primary: 'Voir la Démo', secondary: 'Planifier une Démo' },
    footer: { tagline: 'Solution ERP complète pour les agences de location modernes.', product: 'Produit', company: 'Entreprise', legal: 'Légal', features: 'Fonctionnalités', pricingLink: 'Tarifs', updates: 'Mises à jour', about: 'À Propos', blog: 'Blog', contact: 'Contact', privacy: 'Confidentialité', terms: 'Conditions', security: 'Sécurité', rights: '© 2026 karia.tn. Tous droits réservés.' },
  },
  ar: {
    nav: { features: 'المميزات', benefits: 'الفوائد', pricing: 'الأسعار', login: 'تسجيل الدخول', getStarted: 'ابدأ الآن' },
    hero: {
      tagline: 'حل ERP متكامل لوكالات تأجير السيارات',
      title1: 'أدر أعمال', title2: 'التأجير الخاصة بك', title3: 'بكل سهولة',
      description: 'من الحجوزات وتتبع الأسطول إلى إدارة العملاء المحتملين والموظفين—karia.tn يوفر كل ما تحتاجه مع تصور بيانات أنيق.',
      cta: 'جرّب العرض التجريبي', ctaSubtext: 'بدون تسجيل • وصول فوري', watchDemo: 'احجز عرضاً توضيحياً', trusted: 'موثوق من وكالات التأجير حول العالم',
      quickSetup: 'إعداد سريع', secure: 'آمن وموثوق', support: 'دعم على مدار الساعة',
    },
    demoForm: {
      title: 'احجز عرضاً توضيحياً',
      subtitle: 'اكتشف كيف يمكن لـ karia.tn تحويل أعمالك',
      agencyName: 'اسم الوكالة',
      yourName: 'اسمك',
      yourRole: 'منصبك',
      fleetSize: 'حجم الأسطول',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      preferredTime: 'وقت الاتصال',
      submit: 'طلب عرض',
      submitting: 'جاري الإرسال...',
      success: 'شكراً! سنتواصل معك قريباً.',
      error: 'حدث خطأ. حاول مرة أخرى.',
      roles: { owner: 'مالك', manager: 'مدير', operations: 'عمليات', other: 'أخرى' },
      fleetSizes: { xs: '1-10', sm: '11-25', md: '26-50', lg: '51-100', xl: '100+' },
      selectRole: 'اختر',
      selectFleetSize: 'اختر',
      phonePlaceholder: 'XX XXX XXX',
      selectTime: 'اختر الوقت',
      timeSlots: { morning: 'صباحاً (9 - 12)', afternoon: 'ظهراً (12 - 3)', evening: 'مساءً (3 - 6)', anytime: 'أي وقت' },
    },
    dashboard: { title: 'لوحة تحكم karia.tn', activeRentals: 'الإيجارات النشطة', fleetSize: 'حجم الأسطول', newLeads: 'عملاء جدد', thisMonth: 'هذا الشهر', bookingActivity: 'نشاط الحجوزات', week: 'الأسبوع' },
    features: {
      label: 'المميزات', title: 'كل ما تحتاجه', description: 'أدوات شاملة مصممة خصيصاً لوكالات تأجير السيارات',
      items: [
        { number: '٠١', title: 'إدارة التأجير', desc: 'أنشئ وتتبع الحجوزات من البداية للنهاية. تذكيرات تلقائية، جدولة مرنة، وإدارة كاملة لدورة التأجير في مكان واحد.' },
        { number: '٠٢', title: 'تتبع الأسطول', desc: 'راقب مخزون سياراتك بالكامل في الوقت الفعلي. تتبع التوفر، جداول الصيانة، وحالة المركبات.' },
        { number: '٠٣', title: 'إدارة العملاء والمحتملين', desc: 'اجمع العملاء المحتملين بكفاءة واحفظ معلومات العملاء التفصيلية. أنشئ ملفات عملاء شاملة وحافظ على علاقات تدفع الأعمال المتكررة.' },
        { number: '٠٤', title: 'إدارة الموظفين', desc: 'نظم فريقك بفعالية مع صلاحيات حسب الأدوار، تتبع الجداول، ومراقبة الأداء.' },
        { number: '٠٥', title: 'تصور البيانات', desc: 'حوّل البيانات المعقدة إلى رؤى جميلة وقابلة للتنفيذ. لوحات تحكم واضحة تجعل فهم مقاييس أعمالك سهلاً.' },
        { number: '٠٦', title: 'الوصول السحابي', desc: 'ادخل لعملياتك من أي مكان. بنية سحابية آمنة تضمن أن بياناتك متاحة ومحمية دائماً.' },
      ],
    },
    benefits: {
      label: 'لماذا تختار karia.tn', title: 'مصمم لوكالات التأجير',
      items: [
        { title: 'وفّر الوقت', desc: 'أتمت المهام المتكررة وركز على تنمية أعمالك' },
        { title: 'زد الإيرادات', desc: 'تتبع أفضل ورؤى تؤدي لتسعير واستخدام محسّن' },
        { title: 'حسّن الكفاءة', desc: 'بسّط العمليات عبر الحجز، الأسطول، وإدارة الفريق' },
        { title: 'أمان مؤسسي', desc: 'تشفير بمستوى البنوك يحافظ على بيانات أعمالك آمنة' },
      ],
      documentation: 'توثيق كامل', documentationDesc: 'تتبع كل تفصيل من الحجز للإرجاع مع سجلات رقمية شاملة.',
      analytics: 'تحليلات فورية', analyticsDesc: 'اتخذ قرارات مبنية على البيانات مع رؤى حية لعملياتك.',
    },
    pricing: {
      label: 'الأسعار', title: 'أسعار بسيطة وشفافة', description: 'ابدأ مجاناً، ترقّ مع نموك',
      plans: [
        { name: 'المبتدئ', price: 'مجاني', desc: 'مثالي للبداية', features: ['حتى 20 مركبة', 'إدارة حجوزات أساسية', 'قاعدة بيانات العملاء', 'دعم بالبريد الإلكتروني'] },
        { name: 'الاحترافي', currency: 'د.ت', price: '99', period: '/شهرياً', desc: 'للأعمال النامية', features: ['مركبات غير محدودة', 'تحليلات متقدمة', 'إدارة الموظفين', 'دعم أولوية', 'تكاملات مخصصة'], featured: true },
        { name: 'المؤسسات', price: 'مخصص', desc: 'للعمليات الكبيرة', features: ['كل شيء في الاحترافي', 'مدير حساب مخصص', 'تطوير مخصص', 'ضمان SLA'] },
      ],
    },
    testimonials: {
      label: 'شهادات العملاء', title: 'محبوب من وكالات التأجير',
      items: [
        { quote: 'karia.tn غيّر طريقة إدارتنا لأسطولنا. الواجهة نظيفة وفريقنا تكيف فوراً.', author: 'أحمد بن علي', role: 'مدير العمليات' },
        { quote: 'أخيراً نظام ERP لا يعقد الأمور. كل ما نحتاجه، بلا زيادة.', author: 'محمد الصغير', role: 'مالك الوكالة' },
        { quote: 'تصور البيانات وحده يستحق. نرى كل أعمالنا بنظرة واحدة.', author: 'فاطمة الزهراء', role: 'محللة أعمال' },
      ],
    },
    cta: { title: 'مستعد للبدء؟', description: 'انضم لوكالات التأجير التي تحول عملياتها مع karia.tn. اطلع على العرض التوضيحي المباشر اليوم—بدون تسجيل.', primary: 'شاهد العرض التوضيحي', secondary: 'جدولة عرض' },
    footer: { tagline: 'حل ERP متكامل لوكالات تأجير السيارات الحديثة.', product: 'المنتج', company: 'الشركة', legal: 'قانوني', features: 'المميزات', pricingLink: 'الأسعار', updates: 'التحديثات', about: 'عنا', blog: 'المدونة', contact: 'اتصل بنا', privacy: 'الخصوصية', terms: 'الشروط', security: 'الأمان', rights: '© 2026 karia.tn. جميع الحقوق محفوظة.' },
  },
};

const featureIcons = [Calendar, Car, Users, Database, BarChart3, Cloud];
const benefitIcons = [Clock, DollarSign, Gauge, Lock];

const featureNodes = [
  { id: 'fleet', label: 'Fleet Management', icon: Car, x: 980, y: 230, color: '#499FDD', main: true },
  { id: 'sms', label: 'SMS Alerts', icon: Bell, x: 1080, y: 120, color: '#FF690F' },
  { id: 'data', label: 'TN Vehicle Data', icon: Database, x: 1020, y: 480, color: '#5EBD81' },
  { id: 'team', label: 'Team Portfolio', icon: Users, x: 720, y: 550, color: '#273B7D' },
  { id: 'sched', label: 'Smart Scheduling', icon: Calendar, x: 1140, y: 350, color: '#FFD32F' },
  { id: 'maint', label: 'Maintenance Hub', icon: Gauge, x: 1160, y: 560, color: '#FF690F' },
  { id: 'sec', label: 'Secure Access', icon: Shield, x: 1160, y: 50, color: '#499FDD' },
  { id: 'auto', label: 'Automation', icon: Zap, x: 880, y: 60, color: '#FF690F' },
];

const ConnectedGraph = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden select-none -z-0 opacity-80 flex justify-center">
    <div className="relative w-full max-w-[1240px] h-[800px]">
      <svg width="100%" height="100%" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute inset-0">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#273B7D" stopOpacity="0" />
            <stop offset="40%" stopColor="#499FDD" stopOpacity="0.8" />
            <stop offset="80%" stopColor="#499FDD" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#FF690F" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Curved Origin Strands - Converging at x=700 */}
        <path d="M0 450 C 200 450, 400 320, 700 320" stroke="url(#lineGradient)" strokeWidth="2.5" strokeOpacity="0.25" fill="none" />
        <path d="M0 200 C 200 200, 400 320, 700 320" stroke="url(#lineGradient)" strokeWidth="1.5" strokeOpacity="0.15" fill="none" />
        <path d="M0 320 L 700 320" stroke="url(#lineGradient)" strokeWidth="4" strokeOpacity="0.1" fill="none" />

        {/* Primary Energy Path to Fleet Management (Extending from hub) */}
        <path d="M700 320 L 980 260" stroke="url(#lineGradient)" strokeWidth="2.5" strokeDasharray="12 6" className="animate-dash-flow" />
        <path d="M700 320 L 980 260" stroke="url(#lineGradient)" strokeWidth="1.2" strokeOpacity="0.2" fill="none" />

        {/* Diverging Energy 'Spurts' - Every node now has a solid foundation + dashed pulse */}
        <path d="M700 320 C 850 320, 950 120, 1080 120" stroke="url(#lineGradient)" strokeWidth="1.2" strokeDasharray="15 5" className="animate-dash-flow-slow" />

        {/* Smart Scheduling Connection (Solid 'Less Fade' line + Dashed pulse) */}
        <path d="M700 320 C 900 320, 1050 320, 1140 320" stroke="url(#lineGradient)" strokeWidth="1.4" strokeOpacity="0.25" fill="none" />
        <path d="M700 320 C 900 320, 1050 320, 1140 320" stroke="url(#lineGradient)" strokeWidth="1.2" strokeDasharray="20 10" className="animate-dash-flow" />

        {/* TN Vehicle Data Connection */}
        <path d="M700 320 C 850 320, 950 480, 1020 480" stroke="url(#lineGradient)" strokeWidth="1.2" strokeOpacity="0.2" fill="none" />
        <path d="M700 320 C 850 320, 950 480, 1020 480" stroke="url(#lineGradient)" strokeWidth="1" strokeDasharray="12 6" className="animate-dash-flow" />

        <path d="M700 320 C 720 320, 720 550, 720 550" stroke="url(#lineGradient)" strokeWidth="0.8" strokeDasharray="8 4" className="animate-dash-flow-slow" />
        <path d="M700 320 C 950 320, 1050 560, 1160 560" stroke="url(#lineGradient)" strokeWidth="1" strokeOpacity="0.3" />
        <path d="M700 320 C 1000 320, 1080 50, 1160 50" stroke="url(#lineGradient)" strokeWidth="0.8" strokeOpacity="0.3" />
        <path d="M700 320 C 800 320, 850 60, 880 60" stroke="url(#lineGradient)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5 5" />

        {/* Cross-mesh connections for depth */}
        <path d="M1020 480 L 1160 560" stroke="#FF690F" strokeWidth="0.5" strokeOpacity="0.1" />
        <path d="M1080 120 L 1160 50" stroke="#499FDD" strokeWidth="0.5" strokeOpacity="0.1" />
        <path d="M980 260 L 880 60" stroke="#499FDD" strokeWidth="0.5" strokeOpacity="0.1" />
      </svg>

      {/* Feature Nodes - Unified coordinate system with SVG */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block">
        {featureNodes.map((node) => (
          <div
            key={node.id}
            className={`absolute bg-white/60 backdrop-blur-xl border border-white/80 p-3 px-5 rounded-[24px] shadow-[0_12px_40px_rgba(39,59,125,0.06)] flex items-center gap-3 animate-float-slow transition-all hover:bg-white/90 hover:scale-105 ${node.main ? 'scale-110 !shadow-[0_20px_50px_rgba(39,59,125,0.12)] border-[#499FDD]/20' : ''}`}
            style={{
              left: `${(node.x / 1200) * 100}%`,
              top: `${(node.y / 800) * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="p-2.5 rounded-2xl bg-white shadow-sm ring-1 ring-slate-100" style={{ color: node.color }}>
              <node.icon className={`${node.main ? 'w-5 h-5' : 'w-4 h-4'}`} />
            </div>
            <span className={`text-slate-700 whitespace-nowrap tracking-tight font-medium ${node.main ? 'text-[13px] font-bold' : 'text-[11px]'}`}>
              {node.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Google Sheets Web App URL - Replace with your deployed Apps Script URL
const GOOGLE_SHEETS_URL = "https://script.google.com/macros/s/AKfycbzBdYjvcnc0serZG6ZK8ItoQEXZAv1SAUY-54kYVeugtcfKS1weP42SOfIHTM5RX-if/exec";

interface DemoFormData {
  agencyName: string;
  yourName: string;
  yourRole: string;
  fleetSize: string;
  phone: string;
  email: string;
  preferredTime: string;
}

const submitToGoogleSheets = async (formData: DemoFormData, language: string): Promise<boolean> => {
  const payload = {
    ...formData,
    phone: `+216${formData.phone}`, // Add country code
    timestamp: new Date().toISOString(),
    language: language,
  };

  console.log('=== SUBMITTING TO GOOGLE SHEETS ===');
  console.log('URL:', GOOGLE_SHEETS_URL);
  console.log('Payload:', JSON.stringify(payload, null, 2));

  try {
    const response = await fetch(GOOGLE_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    console.log('Response type:', response.type);
    console.log('Response status:', response.status);
    console.log('=== SUBMISSION COMPLETE (no-cors mode) ===');

    // With mode: 'no-cors', we can't read the response, so we assume success if no error thrown
    return true;
  } catch (error) {
    console.error('=== ERROR SUBMITTING TO GOOGLE SHEETS ===');
    console.error('Error:', error);
    return false;
  }
};

const LandingPageShowcase = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState<Language>('fr');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<DemoFormData>({
    agencyName: '',
    yourName: '',
    yourRole: '',
    fleetSize: '',
    phone: '',
    email: '',
    preferredTime: '',
  });

  const handleFormChange = (field: keyof DemoFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitDemoForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    console.log('Form submitted with data:', formData);

    const success = await submitToGoogleSheets(formData, lang);

    if (success) {
      setFormStatus('success');
      setTimeout(() => {
        setDemoModalOpen(false);
        setFormStatus('idle');
        setFormData({
          agencyName: '',
          yourName: '',
          yourRole: '',
          fleetSize: '',
          phone: '',
          email: '',
          preferredTime: '',
        });
      }, 2000);
    } else {
      setFormStatus('error');
    }
  };

  const t = translations[lang];
  const isRTL = lang === 'ar';

  const langLabels: Record<Language, { flagClass: string; name: string; short: string }> = {
    en: { flagClass: 'fi fi-us', name: 'English', short: 'EN' },
    fr: { flagClass: 'fi fi-fr', name: 'Français', short: 'FR' },
    ar: { flagClass: 'fi fi-tn', name: 'العربية', short: 'عربي' }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Minimal Navigation */}
      <nav className="sticky top-0 bg-white/95 backdrop-blur-lg z-50">
        <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="text-4xl md:text-5xl" style={{ fontFamily: 'Caveat, cursive', color: '#273B7D', fontWeight: 700, letterSpacing: '-0.02em' }}>Karia</a>
          <div className="hidden md:flex items-center gap-12" style={{ fontFamily: 'Inter, sans-serif' }}>
            <a href="#features" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.features}</a>
            <a href="#benefits" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.benefits}</a>
            <a href="#pricing" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.pricing}</a>
            <a href="/about" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">About</a>
            <div className="flex items-center gap-4 ml-6">
              <a href="https://app.karia.tn/login" className="text-base font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-slate-100" style={{ color: '#273B7D' }}>{t.nav.login}</a>
              <a href="https://app.karia.tn/demo?code=mdshamal" className="text-base font-semibold text-white px-7 py-3 rounded-lg transition-all hover:opacity-90" style={{ backgroundColor: '#FF690F' }}>
                See Live Demo
              </a>
              {/* Language Switcher with Flags */}
              <div className="relative">
                <button onClick={() => setLangMenuOpen(!langMenuOpen)} className="flex items-center gap-2 text-base font-medium text-slate-600 hover:text-slate-900 transition-colors px-4 py-2.5 rounded-full hover:bg-slate-100 border border-slate-200">
                  <span className={langLabels[lang].flagClass} style={{ fontSize: '1.25rem' }}></span>
                  <span>{langLabels[lang].name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {langMenuOpen && (
                  <div className="absolute top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 py-3 min-w-[160px]" style={{ right: isRTL ? 'auto' : 0, left: isRTL ? 0 : 'auto' }}>
                    {(['en', 'fr', 'ar'] as Language[]).map((l) => (
                      <button key={l} onClick={() => { setLang(l); setLangMenuOpen(false); }} className={`w-full px-4 py-3 text-base text-left hover:bg-slate-50 transition-colors flex items-center gap-3 ${lang === l ? 'font-semibold bg-slate-50' : ''}`} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <span className={langLabels[l].flagClass} style={{ fontSize: '1.25rem' }}></span>
                        <span>{langLabels[l].name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white shadow-lg">
            <div className="px-4 py-6 space-y-4">
              <a href="#features" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.features}</a>
              <a href="#benefits" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.benefits}</a>
              <a href="#pricing" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.pricing}</a>
              <a href="/about" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">About</a>
              {/* Mobile Language Switcher */}
              <div className="flex gap-2 py-2">
                {(['en', 'fr', 'ar'] as Language[]).map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`px-4 py-2.5 text-sm rounded-lg border transition-colors flex items-center gap-2 ${lang === l ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600'}`}>
                    <span className={langLabels[l].flagClass} style={{ fontSize: '1rem' }}></span>
                    <span>{langLabels[l].short}</span>
                  </button>
                ))}
              </div>
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <a href="https://app.karia.tn/login" className="block text-center py-2.5 font-medium rounded-lg border border-slate-200" style={{ color: '#273B7D' }}>{t.nav.login}</a>
                <a href="https://app.karia.tn/demo?code=mdshamal" className="block w-full text-center text-white font-semibold px-5 py-2.5 rounded-lg transition-all" style={{ backgroundColor: '#FF690F' }}>
                  See Live Demo
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Shifted Left & Top */}
      <section className="relative w-full bg-white pt-6 pb-20 md:pt-10 md:pb-28 overflow-hidden">
        <ConnectedGraph />
        <div className="max-w-[1700px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="relative z-10 lg:col-span-8">
            <p className="text-sm md:text-base uppercase tracking-widest text-slate-500 mb-8 font-semibold">
              {t.hero.tagline}
            </p>
            <h1 className="text-6xl md:text-8xl lg:text-[110px] font-light mb-12 leading-[0.9] tracking-tight -ml-1 md:-ml-2" style={{ color: '#273B7D' }}>
              {t.hero.title1}
              <br />
              {t.hero.title2}
              <br />
              <span className="font-semibold italic" style={{ color: '#FF690F' }}>{t.hero.title3}</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-2xl leading-relaxed font-light">
              {t.hero.description}
            </p>
            <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-6 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <div className="flex flex-col items-center">
                <a href="https://app.karia.tn/demo?code=mdshamal" className="text-white px-10 py-4 rounded-full transition-all duration-300 text-base font-semibold shadow-[0_10px_20px_-5px_rgba(255,105,15,0.4)] hover:shadow-[0_15px_30px_-5px_rgba(255,105,15,0.5)] hover:-translate-y-0.5 active:translate-y-0 inline-block text-center" style={{ backgroundColor: '#FF690F' }}>
                  {t.hero.cta}
                </a>
                <span className="text-xs text-slate-500 mt-2 font-medium">{t.hero.ctaSubtext}</span>
              </div>
              <button
                onClick={() => setDemoModalOpen(true)}
                className={`flex items-center gap-3 transition-all duration-300 text-base font-medium hover:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}
                style={{ color: '#273B7D' }}
              >
                <span>{t.hero.watchDemo}</span>
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-20 pt-12 border-t border-slate-100">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-8 font-bold">{t.hero.trusted}</p>
              <div className={`flex flex-wrap items-center gap-10 text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-2 group cursor-default ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-[#5EBD81] transition-transform group-hover:scale-110" />
                  <span className="text-sm font-medium transition-colors group-hover:text-slate-600">{t.hero.quickSetup}</span>
                </div>
                <div className={`flex items-center gap-2 group cursor-default ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-[#5EBD81] transition-transform group-hover:scale-110" />
                  <span className="text-sm font-medium transition-colors group-hover:text-slate-600">{t.hero.secure}</span>
                </div>
                <div className={`flex items-center gap-2 group cursor-default ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-[#5EBD81] transition-transform group-hover:scale-110" />
                  <span className="text-sm font-medium transition-colors group-hover:text-slate-600">{t.hero.support}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right side is now empty to let the ConnectedGraph shine */}
          <div className="lg:col-span-4" />
        </div>
      </section>

      {/* Visual Dashboard Preview - Hero Image */}
      <section className="max-w-6xl mx-auto px-6 pb-32">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-6 py-3 flex items-center gap-2" style={{ backgroundColor: '#273B7D' }}>
            <div className="flex gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#1a2a5a' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#1a2a5a' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#1a2a5a' }} />
            </div>
            <span className={`text-slate-400 text-xs ${isRTL ? 'mr-4' : 'ml-4'}`}>{t.dashboard.title}</span>
          </div>
          <div className="p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
              {[
                { label: t.dashboard.activeRentals, value: "147", icon: Car },
                { label: t.dashboard.fleetSize, value: "92", icon: Database },
                { label: t.dashboard.newLeads, value: "58", icon: Users },
                { label: t.dashboard.thisMonth, value: "$64k", icon: TrendingUp }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 border border-slate-200">
                  <stat.icon className="w-5 h-5 text-slate-400 mb-3" />
                  <div className="text-3xl font-light text-slate-900 mb-1">{stat.value}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl p-8 border border-slate-200">
              <h3 className="text-sm uppercase tracking-wider text-slate-500 mb-6">{t.dashboard.bookingActivity}</h3>
              <div className="space-y-4">
                {[75, 90, 65, 95, 80, 70].map((width, idx) => (
                  <div key={idx} className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-16 text-xs text-slate-400">{t.dashboard.week} {idx + 1}</div>
                    <div className="flex-1 bg-slate-100 rounded-full h-2">
                      <div
                        className="bg-slate-900 h-2 rounded-full transition-all"
                        style={{ width: `${width}%`, marginLeft: isRTL ? 'auto' : 0, marginRight: isRTL ? 0 : 'auto' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Clean Grid */}
      <section id="features" className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-32">
          <div className="mb-20">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.features.label}</p>
            <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ color: '#273B7D' }}>
              {t.features.title}
            </h2>
            <p className="text-xl text-slate-600 font-light max-w-2xl">
              {t.features.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-16 gap-y-20">
            {t.features.items.map((feature, idx) => {
              const IconComponent = featureIcons[idx];
              return (
                <div key={idx} className="group">
                  <div className={`flex items-start gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="text-6xl font-light text-slate-200 group-hover:text-slate-300 transition-colors">
                      {feature.number}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center mb-4 group-hover:border-slate-900 transition-colors">
                        <IconComponent className="w-5 h-5 text-slate-400 group-hover:text-slate-900 transition-colors" />
                      </div>
                      <h3 className="text-2xl font-medium mb-3 text-slate-900">{feature.title}</h3>
                      <p className="text-slate-600 leading-relaxed font-light">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section >

      {/* Benefits Section - Split Layout */}
      < section id="benefits" className="border-t border-slate-200 bg-slate-50" >
        <div className="max-w-6xl mx-auto px-6 py-32">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.benefits.label}</p>
              <h2 className="text-5xl font-light mb-8" style={{ color: '#273B7D' }}>
                {t.benefits.title}
              </h2>
              <div className="space-y-8">
                {t.benefits.items.map((benefit, idx) => {
                  const IconComponent = benefitIcons[idx];
                  return (
                    <div key={idx} className={`flex gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900 mb-1">{benefit.title}</h3>
                        <p className="text-slate-600 font-light">{benefit.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-32">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.pricing.label}</p>
            <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ color: '#273B7D' }}>
              {t.pricing.title}
            </h2>
            <p className="text-xl text-slate-600 font-light">
              {t.pricing.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {t.pricing.plans.map((plan, idx) => (
              <div
                key={idx}
                className={`rounded-2xl p-8 ${plan.featured
                  ? 'bg-slate-900 text-white border-2 border-slate-900'
                  : 'bg-white border border-slate-200'
                  }`}
              >
                <h3 className="text-xl font-medium mb-2">{plan.name}</h3>
                <p className={`text-sm mb-6 ${plan.featured ? 'text-slate-300' : 'text-slate-500'}`}>
                  {plan.desc}
                </p>
                <div className="mb-8 text-center">
                  {plan.currency && plan.price !== 'Free' && plan.price !== 'Gratuit' && plan.price !== 'مجاني' && plan.price !== 'Custom' && plan.price !== 'Sur Mesure' && plan.price !== 'مخصص' ? (
                    <div className="flex items-baseline gap-1 justify-center">
                      <span className={`text-sm font-medium ${plan.featured ? 'text-slate-300' : 'text-slate-600'}`}>{plan.currency}</span>
                      <span className="text-5xl font-light">{plan.price}</span>
                    </div>
                  ) : (
                    <span className="text-5xl font-light">{plan.price}</span>
                  )}
                  {plan.period && <span className={`text-lg ${plan.featured ? 'text-slate-400' : 'text-slate-500'}`}>{plan.period}</span>}
                </div>
                <button className={`w-full py-3 rounded-full transition-colors mb-8 ${plan.featured
                  ? 'bg-white text-slate-900 hover:bg-slate-100'
                  : 'border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
                  }`}>
                  {t.nav.getStarted}
                </button>
                <ul className="space-y-3">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx} className={`flex items-center gap-3 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 ${plan.featured ? 'text-slate-300' : 'text-slate-400'}`} />
                      <span className={plan.featured ? 'text-slate-200' : 'text-slate-600'}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-32">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.testimonials.label}</p>
            <h2 className="text-5xl font-light" style={{ color: '#273B7D' }}>
              {t.testimonials.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.testimonials.items.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 border border-slate-200">
                <p className="text-slate-700 mb-6 leading-relaxed font-light italic">
                  "{testimonial.quote}"
                </p>
                <div className="pt-6 border-t border-slate-100">
                  <p className="font-medium text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-32">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8" style={{ color: '#273B7D' }}>
              {t.cta.title}
            </h2>
            <p className="text-xl text-slate-600 mb-12 font-light leading-relaxed">
              {t.cta.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://app.karia.tn/demo?code=mdshamal" className="bg-slate-900 text-white hover:bg-slate-800 px-10 py-4 rounded-full text-lg transition-colors">
                {t.cta.primary}
              </a>
              <button className="border border-slate-300 text-slate-900 hover:border-slate-900 px-10 py-4 rounded-full text-lg transition-colors">
                {t.cta.secondary}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <a href="/" className="text-2xl mb-4 inline-block" style={{ fontFamily: 'Caveat, cursive', color: '#273B7D', fontWeight: 700 }}>karia.tn</a>
              <p className="text-sm text-slate-600 font-light">
                {t.footer.tagline}
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4 text-sm uppercase tracking-wider text-slate-900">{t.footer.product}</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.features}</a></li>
                <li><a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.pricingLink}</a></li>
                <li><a href="/updates" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.updates}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4 text-sm uppercase tracking-wider text-slate-900">{t.footer.company}</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="/about" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.about}</a></li>
                {/* <li><a href="/blog" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.blog}</a></li> */}
                <li><a href="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.contact}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4 text-sm uppercase tracking-wider text-slate-900">{t.footer.legal}</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="/privacy" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.privacy}</a></li>
                <li><a href="/terms" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.terms}</a></li>
                <li><a href="/security" className="text-slate-600 hover:text-slate-900 transition-colors">{t.footer.security}</a></li>
              </ul>
            </div>
          </div>
          <div className={`pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <p className="text-sm text-slate-500">{t.footer.rights}</p>
            {/* <div className="flex gap-6 text-sm text-slate-500">
              <a href="https://twitter.com/kariaTN" className="hover:text-slate-900 transition-colors">Twitter</a>
              <a href="https://linkedin.com/company/kariaTN" className="hover:text-slate-900 transition-colors">LinkedIn</a>
              <a href="https://github.com/kariaTN" className="hover:text-slate-900 transition-colors">GitHub</a>
            </div> */}
          </div>
        </div>
      </footer>

      {/* Schedule Demo Modal */}
      {demoModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-start justify-center p-4 pt-8 overflow-y-auto" onClick={() => setDemoModalOpen(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center rounded-t-2xl relative" style={{ backgroundColor: '#273B7D' }}>
              <div className="text-center flex-1">
                <h3 className="text-2xl font-bold text-white">{t.demoForm.title}</h3>
                <p className="text-sm text-white/80 mt-1">{t.demoForm.subtitle}</p>
              </div>
              <button
                onClick={() => setDemoModalOpen(false)}
                className="text-white/70 hover:text-white transition-colors p-2 absolute top-4 right-4"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmitDemoForm} className="p-8 space-y-5">
              {formStatus === 'success' ? (
                <div className="text-center py-10">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#5EBD81' }} />
                  <p className="text-xl font-semibold text-slate-900">{t.demoForm.success}</p>
                </div>
              ) : (
                <>
                  {/* Agency Name */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.agencyName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.agencyName}
                      onChange={(e) => handleFormChange('agencyName', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none transition-all"
                      placeholder="Your agency name"
                    />
                  </div>

                  {/* Your Name */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.yourName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.yourName}
                      onChange={(e) => handleFormChange('yourName', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none transition-all"
                      placeholder="Your full name"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.yourRole} <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.yourRole}
                      onChange={(e) => handleFormChange('yourRole', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none bg-white transition-all"
                    >
                      <option value="">{t.demoForm.selectRole}</option>
                      <option value="owner">{t.demoForm.roles.owner}</option>
                      <option value="manager">{t.demoForm.roles.manager}</option>
                      <option value="operations">{t.demoForm.roles.operations}</option>
                      <option value="other">{t.demoForm.roles.other}</option>
                    </select>
                  </div>

                  {/* Fleet Size */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.fleetSize} <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.fleetSize}
                      onChange={(e) => handleFormChange('fleetSize', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none bg-white transition-all"
                    >
                      <option value="">{t.demoForm.selectFleetSize}</option>
                      <option value="1-10">{t.demoForm.fleetSizes.xs}</option>
                      <option value="11-25">{t.demoForm.fleetSizes.sm}</option>
                      <option value="26-50">{t.demoForm.fleetSizes.md}</option>
                      <option value="51-100">{t.demoForm.fleetSizes.lg}</option>
                      <option value="100+">{t.demoForm.fleetSizes.xl}</option>
                    </select>
                  </div>

                  {/* Phone Number with +216 prefix */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.phone} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 text-base text-slate-700 bg-slate-100 border border-r-0 border-slate-300 rounded-l-xl font-semibold">
                        +216
                      </span>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                          handleFormChange('phone', value);
                        }}
                        className="flex-1 px-4 py-3 text-base border border-slate-300 rounded-r-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none transition-all"
                        placeholder={t.demoForm.phonePlaceholder}
                        maxLength={8}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.email}
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none transition-all"
                      placeholder="email@example.com"
                    />
                  </div>

                  {/* Preferred Time - Simple Dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t.demoForm.preferredTime}
                    </label>
                    <select
                      value={formData.preferredTime}
                      onChange={(e) => handleFormChange('preferredTime', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#499FDD] focus:border-transparent outline-none bg-white transition-all"
                    >
                      <option value="">{t.demoForm.selectTime}</option>
                      <option value="morning">{t.demoForm.timeSlots.morning}</option>
                      <option value="afternoon">{t.demoForm.timeSlots.afternoon}</option>
                      <option value="evening">{t.demoForm.timeSlots.evening}</option>
                      <option value="anytime">{t.demoForm.timeSlots.anytime}</option>
                    </select>
                  </div>

                  {/* Error Message */}
                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      {t.demoForm.error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full text-white px-8 py-4 rounded-xl transition-all duration-300 text-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 mt-4"
                    style={{ backgroundColor: '#FF690F' }}
                  >
                    {formStatus === 'submitting' ? t.demoForm.submitting : t.demoForm.submit}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageShowcase;